import React from 'react';
import {
    Card, CardContent, Avatar, Typography, Box, Chip,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, alpha, useTheme,
} from '@mui/material';
import { Article as ArticleIcon } from '@mui/icons-material';

/**
 * Full-width table showing per-platform breakdown:
 * Platform | Posts | Views | Likes | Comments | Shares | Engagement %
 *
 * Props
 *   chartData – transformed platform array
 */
const PlatformTable = ({ chartData }) => {
    const theme = useTheme();
    const HEADERS = ['Platform', 'Posts', 'Views', 'Likes', 'Comments', 'Shares', 'Engagement %'];

    return (
        <Card
            elevation={0}
            sx={{
                background: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 4,
            }}
        >
            <CardContent sx={{ p: 3.5 }}>
                {/* Card header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            width: 44,
                            height: 44,
                            mr: 2,
                        }}
                    >
                        <ArticleIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Platform Breakdown
                    </Typography>
                </Box>

                {/* Table */}
                <TableContainer component={Paper} elevation={0} sx={{ background: 'transparent' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {HEADERS.map((h) => (
                                    <TableCell
                                        key={h}
                                        sx={{
                                            fontWeight: 700,
                                            color: 'text.secondary',
                                            fontSize: '0.78rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            borderBottom: `2px solid ${theme.palette.divider}`,
                                            py: 1.5,
                                        }}
                                    >
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {chartData.map((row) => (
                                <TableRow
                                    key={row.rawPlatform}
                                    sx={{
                                        '&:last-child td': { border: 0 },
                                        '&:hover': { bgcolor: alpha(row.color, 0.04) },
                                        transition: 'background 0.15s',
                                    }}
                                >
                                    {/* Platform name + colour dot */}
                                    <TableCell sx={{ py: 1.75 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 10, height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: row.color,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 600, color: 'text.primary' }}
                                            >
                                                {row.platform}
                                            </Typography>
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ color: 'text.secondary', py: 1.75 }}>
                                        {row.posts.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary', py: 1.75 }}>
                                        {row.views.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary', py: 1.75 }}>
                                        {row.likes.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary', py: 1.75 }}>
                                        {row.comments.toLocaleString()}
                                    </TableCell>
                                    <TableCell sx={{ color: 'text.secondary', py: 1.75 }}>
                                        {row.shares.toLocaleString()}
                                    </TableCell>

                                    {/* Engagement chip */}
                                    <TableCell sx={{ py: 1.75 }}>
                                        <Chip
                                            label={`${row.engagement}%`}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha(row.color, 0.12),
                                                color: row.color,
                                                fontWeight: 700,
                                                fontSize: '0.75rem',
                                                border: `1px solid ${alpha(row.color, 0.25)}`,
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

export default PlatformTable;
